// Which of the four states a show is filed under, in one place.
//
// ---------------------------------------------------------------------------
// TWO PAGES DISAGREEING ABOUT ONE ROW IS WORSE THAN EITHER ANSWER
//
// The Activations list files a show under Open, Paused, Closed or Sealed, and Show
// details states the same fact about the same row. They were separate copies of
// the same three tests for about a day, which is exactly long enough for one of
// them to gain a case the other does not have — and the failure that produces
// is not a wrong screen, it is two screens that are each internally consistent
// and disagree with each other, which is the kind a person resolves by deciding
// the software is unreliable rather than by reporting a bug.
//
// ---------------------------------------------------------------------------
// A SEAL OUTRANKS A CLOSE, AND THE ORDER OF THE TESTS IS THE RULING
//
// operator/src/activations.ts: a seal without a close is still a seal. So a row
// carrying both is Sealed, not Closed. The difference is not cosmetic — the
// seal is what the export reads before it will produce a file, and it is the
// one that cannot be undone.
//
// ---------------------------------------------------------------------------
// FOUR SINCE 12 OCTOBER (decision C3), AND THE SERVICE DECIDES WHICH
//
// The prototype's states are active, paused and archived. The ruling kept
// Open, Closed and Sealed and added Paused, which the service now holds as
// activation.paused_at (151): a paused show takes nothing new from anybody
// until it is resumed, and keeps everything it has. Archived is still not a
// state, because it promises a reversal that a seal specifically does not
// offer.
//
// The row carries `state`, decided once in operator/src/activations.ts, and
// this reads it. The timestamps are the fallback for a row from a service that
// predates the field, tested in the same order: a seal outranks a pause, and a
// pause outranks a close.

import type { ActivationLine } from "../../../operator/src/activations.js";

/** The four a show can be in. Ordered as the filter offers them. */
export type ShowState = "Open" | "Paused" | "Closed" | "Sealed";

export const SHOW_STATES: readonly ShowState[] = ["Open", "Paused", "Closed", "Sealed"];

const NAMED: Record<string, ShowState> = { open: "Open", paused: "Paused", closed: "Closed", sealed: "Sealed" };

/** Which one this row is: the service's word, or the timestamps where it sent none. */
export function lifecycle(activation: ActivationLine): ShowState {
  const named = NAMED[String((activation as { state?: unknown }).state ?? "")];
  if (named !== undefined) return named;
  if (said(activation.sealed_at) !== "") return "Sealed";
  if (said((activation as { paused_at?: string | null }).paused_at) !== "") return "Paused";
  if (said(activation.closed_at) !== "") return "Closed";
  return "Open";
}

/** The service's word for a state, for a filter sent to it. */
export function stateParam(state: ShowState): string {
  return state.toLowerCase();
}

/** Which tag a state wears. Open reads as good, paused and closed as between, sealed as finished. */
export const STATE_TAG: Record<ShowState, string> = {
  Open: "tag-good",
  Paused: "tag-warn",
  Closed: "tag-warn",
  Sealed: "tag-off",
};

/**
 * What being in that state actually means, for the page that has room to say.
 *
 * The list has room for a word and Show details has room for a sentence, which
 * is why this is separate from the tag rather than folded into it.
 */
export const STATE_MEANS: Record<ShowState, string> = {
  Open: "Not recorded as closed. A stand may still be publishing against it.",
  Paused:
    "Kept exactly as it is and taken off the air. Registration, passcodes, plays, check-ins and screen pulls are refused, the screens say the stand is paused, and nothing is deleted.",
  Closed: "Recorded as finished, and not sealed. What was captured stays readable.",
  Sealed: "The record is final. Nothing further is captured against it, and the export reads it as closed.",
};

const said = (value: string | null | undefined): string => (typeof value === "string" ? value.trim() : "");
